/* eslint-disable unicorn/no-null */
import { create } from 'zustand'

import { API_CONFIG, API_ENDPOINTS } from '@/config/api'
import { apiClient } from '@/config/apiClient'

export type OptInState = 'idle' | 'present' | 'missing' | 'error'

export const MARKETING_CONSENT = {
  EMAIL: 'EMAIL',
  SMS: 'SMS',
  DV1: 'DV1',
  DV2: 'DV2',
  KYC: 'KYC',
  WHATSAPP: 'WHATSAPP',
  MESSENGER: 'MESSENGER',
  INSTAGRAM: 'INSTAGRAM',
  CONTACT: 'CONTACT',
  NO_ALERT: 'NO_ALERT',
} as const

export type MarketingConsentType =
  (typeof MARKETING_CONSENT)[keyof typeof MARKETING_CONSENT]

interface ProfileData {
  [key: string]: unknown
}

interface ProfileState {
  optInState: OptInState
  profile: ProfileData | null
  isLoading: boolean
  error: string | null
}

interface ProfileActions {
  fetchProfile: () => Promise<void>
  submitOptIn: () => Promise<void>
  submitConsent: (consentData: ConsentPayload) => Promise<void>
  reset: () => void
}

export interface ConsentPayload {
  name: MarketingConsentType
  value: '0' | '1'
  email?: string
  phone?: string
  token?: string
}

export const useProfileStore = create<ProfileState & ProfileActions>(set => ({
  optInState: 'idle',
  profile: null,
  isLoading: false,
  error: null,

  fetchProfile: async () => {
    set({ isLoading: true, error: null })
    try {
      const profile = (await apiClient.get(
        API_ENDPOINTS.profile(API_CONFIG.programCode)
      )) as ProfileData
      set({ optInState: 'present', profile, isLoading: false })
    } catch (error: unknown) {
      const status = (error as Record<string, unknown>)?.status
      if (status === 409) {
        set({ optInState: 'missing', isLoading: false })
      } else {
        set({ optInState: 'error', error: String(error), isLoading: false })
      }
    }
  },

  submitOptIn: async () => {
    set({ isLoading: true, error: null })
    try {
      await apiClient.post(API_ENDPOINTS.participation(API_CONFIG.programCode))
      set({ isLoading: false })
    } catch (error: unknown) {
      set({ error: String(error), isLoading: false })
      throw error
    }
  },

  submitConsent: async (consentData: ConsentPayload) => {
    set({ isLoading: true, error: null })
    try {
      await apiClient.post(
        API_ENDPOINTS.consents(API_CONFIG.programCode),
        consentData
      )
      set({ isLoading: false })
    } catch {
      // Intentionally ignored — consent errors don't block the flow
      set({ isLoading: false })
    }
  },

  reset: () =>
    set({
      optInState: 'idle',
      profile: null,
      isLoading: false,
      error: null,
    }),
}))
